import { useEffect, useState } from "react";
import { CalendarRange, Clock3, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import HUDFrame from "@/components/HUDFrame";

const dayLabel = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const dayIdx = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 };

const loadTone = (s) =>
  s === "green" ? "bg-signalGreen" : s === "red" ? "bg-signalRed" : "bg-signalAmber";

const compTone = (load) =>
  load < 55
    ? "text-signalGreen border-signalGreen/50"
    : load < 80
      ? "text-signalAmber border-signalAmber/50"
      : "text-signalRed border-signalRed/50";

const compLabel = (load) => (load < 55 ? "low" : load < 80 ? "med" : "high");

const minutesToLabel = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
};

function nextOccurrenceISO(weekdayKey, hour = 17) {
  const target = dayIdx[weekdayKey];
  const now = new Date();
  const days = (target - now.getDay() + 7) % 7 || 7;
  const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  dt.setHours(hour, 0, 0, 0);
  return dt.toISOString();
}

export default function Planner() {
  const [profile, setProfile] = useState(null);
  const [signals, setSignals] = useState(null);
  const [calStatus, setCalStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [busyDay, setBusyDay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/profile"), api.get("/signals"), api.get("/calendar/status")])
      .then(([p, s, c]) => {
        if (cancelled) return;
        setProfile(p.data);
        setSignals(s.data);
        setCalStatus(c.data);
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Planner unavailable."));
    return () => {
      cancelled = true;
    };
  }, []);

  const addBuffer = async (dayKey) => {
    setBusyDay(dayKey);
    try {
      const startIso = nextOccurrenceISO(dayKey, 17);
      const { data } = await api.post("/calendar/events/create", {
        summary: `Buffer window — ${dayLabel[dayKey]}`,
        start: startIso,
        duration_min: 60,
        category: "buffer",
        description: "Life OS · buffer between commitments. Guarded by the tower.",
      });
      toast.success("Buffer added on your calendar.", {
        action: data.html_link ? { label: "Open", onClick: () => window.open(data.html_link, "_blank") } : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add buffer.");
    } finally {
      setBusyDay(null);
    }
  };

  if (err) return <div className="mx-auto max-w-3xl px-6 py-16 text-mist" data-testid="planner-error">{String(err)}</div>;
  if (!profile || !signals)
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist" data-testid="planner-loading">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Composing the week…
      </div>
    );

  const commitments = (profile.commitments || []).filter((c) => c.category !== "sleep");
  const canWrite = !!calStatus?.can_write;

  return (
    <div data-testid="planner-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">P-05 · Planner</p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        The true cost of
        <br />
        <span className="italic text-mist">the week you scheduled.</span>
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-mist">
        Every commitment carries setup and aftermath. The Planner shows their
        true landing footprint against your household&apos;s capacity.
      </p>

      {/* Week bar */}
      <HUDFrame className="mt-10 bg-deep/40 p-8" tone="teal">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <CalendarRange className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Week landing forecast
          </div>
          {canWrite && (
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
              Hover any day to add a 1h buffer on your calendar
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-7 gap-3" data-testid="planner-week">
          {signals.day_loads.map((d) => (
            <div key={d.day} className="group flex flex-col items-center gap-2">
              <div className="flex h-40 w-full items-end bg-rule/40">
                <div
                  className={`w-full ${loadTone(d.status)}`}
                  style={{ height: `${Math.max(4, d.load)}%` }}
                  title={`${d.load}%`}
                />
              </div>
              <div className="text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                  {dayLabel[d.day]}
                </div>
                <div className="font-mono text-sm text-ink">{d.load}%</div>
              </div>
              {canWrite && (
                <button
                  onClick={() => addBuffer(d.day)}
                  disabled={busyDay === d.day}
                  data-testid={`planner-add-buffer-${d.day}`}
                  className="inline-flex items-center gap-1 border border-rule bg-obsidian px-2 py-1 font-mono text-[9px] uppercase tracking-[0.22em] text-mist opacity-0 transition-opacity duration-200 hover:border-teal hover:text-ink group-hover:opacity-100 disabled:opacity-50"
                  title={`Add a 1h buffer to next ${dayLabel[d.day]}`}
                >
                  <Plus className="h-3 w-3" strokeWidth={1.5} />
                  Buffer
                </button>
              )}
            </div>
          ))}
        </div>
      </HUDFrame>

      {/* Commitments table */}
      <HUDFrame className="mt-8 bg-deep/40 p-8" tone="teal">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          <Clock3 className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
          Standing commitments · landing impact
        </div>

        {commitments.length === 0 ? (
          <p className="mt-6 text-sm text-mist">
            No standing commitments yet. Add some in onboarding to see landing impact.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-rule text-mist">
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Days</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Commitment</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Category</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Start</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Duration</th>
                  <th className="py-3 text-right font-mono text-[10px] uppercase tracking-[0.22em]">
                    Peak day load
                  </th>
                </tr>
              </thead>
              <tbody>
                {commitments.map((c) => {
                  const peakLoad = Math.max(
                    ...signals.day_loads
                      .filter((d) => c.days.includes(d.day))
                      .map((d) => d.load),
                    0
                  );
                  return (
                    <tr
                      key={c.commitment_id}
                      className="border-b border-rule/60 last:border-b-0 hover:bg-obsidian/40"
                    >
                      <td className="py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-teal">
                        {c.days.map((d) => dayLabel[d]).join(" · ")}
                      </td>
                      <td className="py-4 text-ink">{c.title || "(untitled)"}</td>
                      <td className="py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
                        {c.category}
                      </td>
                      <td className="py-4 font-mono text-sm text-mist">
                        {minutesToLabel(c.start_min)}
                      </td>
                      <td className="py-4 font-mono text-sm text-mist">{c.duration_min}m</td>
                      <td className="py-4 text-right">
                        <span
                          className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${compTone(peakLoad)}`}
                        >
                          {compLabel(peakLoad)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </HUDFrame>
    </div>
  );
}
